import { BookOpenCheck, Compass } from "lucide-react";
import { useScenario } from "../simulation/ScenarioContext";
import { audienceDefinitions } from "./audience";
import { useAudience } from "./AudienceContext";
import { depthDefinitions } from "./depth";
import { useDepth } from "./DepthContext";
import { resolveDiveGuidance } from "./diveGuidance";
import {
  fieldGuidanceRegistry,
  resolveFieldGuidance,
  type GuidanceSurface,
} from "./fieldGuidance";
import "./PerspectiveFieldGuide.css";

export function PerspectiveFieldGuide({
  surface,
  contextLabel,
}: {
  surface: GuidanceSurface;
  contextLabel?: string;
}) {
  const { audience } = useAudience();
  const { depth } = useDepth();
  const { stage } = useScenario();
  const guide = fieldGuidanceRegistry[surface];
  const dive = resolveDiveGuidance({
    surface,
    audience,
    depth,
    scenarioStage: stage,
  });

  return (
    <section
      aria-label={`${contextLabel ?? guide.title} contextual guidance`}
      className="perspective-field-guide"
      data-dive-stage={dive.diveStage}
      key={`${surface}-${audience}-${depth}-${stage}`}
    >
      <div className="dive-guidance">
        <div className="dive-guidance__heading">
          <span><Compass aria-hidden="true" size={18} /> DIVE · {dive.label}</span>
          <strong>{dive.routePrompt}</strong>
        </div>
        <div className="dive-guidance__context">
          <p>{dive.audiencePrompt}</p>
          <p>{dive.depthPrompt}</p>
          <small><strong>Complete when:</strong> {dive.completionSignal}</small>
        </div>
      </div>

      <details className="perspective-field-guide__details">
        <summary>
          <span><BookOpenCheck aria-hidden="true" size={18} /> Field definitions</span>
          <strong>{contextLabel ?? guide.title}</strong>
          <small>
            {audienceDefinitions[audience].label} · {depthDefinitions[depth].label}
          </small>
        </summary>
        <p className="perspective-field-guide__intro">
          Expand definitions only when a visible field needs context. Audience changes language,
          depth changes detail, and neither changes permissions or scenario state.
        </p>
        <dl className="perspective-field-guide__fields">
          {guide.fields.map((item) => {
            const resolved = resolveFieldGuidance(surface, item.id, audience, depth);
            return (
              <div key={resolved.id}>
                <dt>{resolved.label}</dt>
                <dd>
                  <strong>{resolved.audienceExplanation}</strong>
                  <span>{resolved.depthExplanation}</span>
                </dd>
              </div>
            );
          })}
        </dl>
      </details>
    </section>
  );
}
